/* eslint-disable @1js/office-online-rules/no-v8-imports-for-fluentui */
import * as React from "react";

import type { IButtonStyles, ITheme } from "@fluentui/react";
import { concatStyleSets, memoizeFunction } from "@fluentui/react";

import type { AppFlyoutAnchorInternalProps } from "@1js/acui-button";
import {
  AppFlyoutAnchor,
  getActionButtonStyles,
  getActionFlyoutAnchorStyles,
} from "@1js/acui-button";
import { isV9ButtonEnabled } from "@1js/acui-experiments";
import { baseFabricFocusAfterSelector } from "@1js/acui-common";
import { MeasuredContext } from "@1js/acui-resize-group";
import { shouldShadowRender } from "@1js/acui-shadow-rendering";
import {
  AppThemeConsumer,
  customTokens,
  isNextStyleEnabled,
} from "@1js/acui-theme";
import { AppContext } from "@1js/acui-context";

import { getOnRenderText } from "../onRenderText";
import type { UseLegacyRibbonButtonProps } from "../RibbonButton";
import { ribbonButtonHighContrastStyles } from "../RibbonButton/RibbonButton.styles";
import type { RibbonControlInternalProps } from "../RibbonControlProps";
import { RibbonFlyoutAnchor as RibbonFlyoutAnchorNext } from "./next";
import {
  useV9RibbonControlsEnabled,
  useNewV9RibbonMenuEnabled,
} from "../../../../hooks";

export type RibbonFlyoutAnchorInternalProps = AppFlyoutAnchorInternalProps &
  RibbonControlInternalProps &
  UseLegacyRibbonButtonProps & { legacyRef?: React.RefObject<AppFlyoutAnchor> };

declare let __Delay_FluentV8__: boolean;

const V8RibbonAppFlyoutAnchor =
  typeof __Delay_FluentV8__ === "undefined" || !__Delay_FluentV8__
    ? AppFlyoutAnchor
    : React.lazy(
        () =>
          import(
            /*webpackChunkName: "commonV8"*/ "@1js/acui-button/lib/AppFlyoutAnchor"
          )
      );

const getStyles = memoizeFunction(
  (
    actionButtonStyles: IButtonStyles,
    flyoutAnchorStyles: IButtonStyles,
    customStyles?: IButtonStyles
  ) => {
    return concatStyleSets(
      actionButtonStyles,
      flyoutAnchorStyles,
      ribbonButtonHighContrastStyles,
      customStyles
    );
  }
);

export const getRibbonFlyoutAnchorStyles = (
  theme: ITheme,
  hideChevron?: boolean,
  customStyles?: IButtonStyles
): IButtonStyles => {
  return getStyles(
    getActionButtonStyles(theme),
    getActionFlyoutAnchorStyles(theme, !!hideChevron),
    customStyles
  );
};

export const getVNextHiddenFlyoutAnchorStyles = ():
  | IButtonStyles
  | undefined => {
  if (!isNextStyleEnabled()) {
    return undefined;
  }

  return {
    root: {
      cursor: "pointer",
      backgroundColor: customTokens.colorNeutralBackground1,
      color: customTokens.colorNeutralForeground1,
      borderRadius: customTokens.borderRadiusMedium,
      height: 32,
      padding: customTokens.spacingHorizontalXS,
      ":active": {
        backgroundColor: customTokens.colorNeutralBackground1Pressed,
        color: customTokens.colorNeutralForeground1Pressed,
      },
    },
    rootHovered: {
      backgroundColor: customTokens.colorNeutralBackground1Hover,
      color: customTokens.colorNeutralForeground1Hover,
    },
    rootPressed: {
      backgroundColor: customTokens.colorNeutralBackground1Pressed,
      color: customTokens.colorNeutralForeground1Pressed,
      borderColor: customTokens.colorNeutralStroke1Pressed,
    },
    rootExpanded: {
      backgroundColor: customTokens.colorNeutralBackground1Selected,
      color: customTokens.colorNeutralForeground1Selected,
      // VSO: 6578941 These two selectors are needed instead of rootexpandedhovered
      // otherwise when expanded+pressed the icon takes the background color from
      // rootexpandedhovered while the button takes the background color from rootexpanded
      ":hover": {
        backgroundColor: customTokens.colorNeutralBackground1Hover,
        color: customTokens.colorNeutralForeground1Hover,
      },
      ":active:hover": {
        backgroundColor: customTokens.colorNeutralBackground1Pressed,
        color: customTokens.colorNeutralForeground1Pressed,
      },
    },
    icon: {
      margin: customTokens.spacingHorizontalNone,
    },
    menuIcon: {
      marginLeft: customTokens.spacingHorizontalXS,
      marginRight: customTokens.spacingHorizontalNone,
    },
    label: {
      margin: customTokens.spacingHorizontalNone,
      padding: customTokens.spacingHorizontalNone,
      paddingLeft: customTokens.spacingHorizontalXS,
    },
    rootFocused: {
      selectors: {
        [baseFabricFocusAfterSelector]: {
          borderRadius: customTokens.borderRadiusMedium,
        },
      },
    },
  };
};

export const RibbonFlyoutAnchorCurrent = React.memo(
  React.forwardRef(
    (
      props: RibbonFlyoutAnchorInternalProps,
      ref: React.RefObject<AppFlyoutAnchor>
    ) => {
      const { shadowRenderMenu } = props;

      return (
        <MeasuredContext.Consumer>
          {({ isMeasured }) => (
            <AppThemeConsumer>
              {(theme: ITheme) => (
                <React.Suspense fallback={<></>}>
                  <V8RibbonAppFlyoutAnchor
                    {...props}
                    menuDefinition={props.menuDefinition}
                    styles={getRibbonFlyoutAnchorStyles(
                      theme,
                      props.hideChevron,
                      (props as AppFlyoutAnchorInternalProps).styles
                    )}
                    ref={props.legacyRef || ref}
                    dataAutomationType={
                      props.dataAutomationType || "RibbonFlyoutAnchor"
                    }
                    onRenderText={
                      props.onRenderText || getOnRenderText(props.size)
                    }
                    persistMenu={true}
                    shadowRenderMenu={shouldShadowRender(
                      shadowRenderMenu,
                      isMeasured
                    )}
                    preventCalloutDismissOnScroll={true}
                  />
                </React.Suspense>
              )}
            </AppThemeConsumer>
          )}
        </MeasuredContext.Consumer>
      );
    }
  )
);
RibbonFlyoutAnchorCurrent.displayName = "RibbonFlyoutAnchorCurrent";

export const RibbonFlyoutAnchor = React.forwardRef<
  HTMLButtonElement,
  RibbonFlyoutAnchorInternalProps
>((props, ref) => {
  const appContext = React.useContext(AppContext);
  const isV9RibbonControlsEnabled = useV9RibbonControlsEnabled();
  const isV9MenuEnabled = useNewV9RibbonMenuEnabled();
  const isNewButtonEnabled =
    (isV9ButtonEnabled(appContext.isExperimentalFeatureEnabled) ||
      isV9RibbonControlsEnabled) &&
    !props.useLegacyButton;

  const finalProps = {
    ...props,
    useLegacyContextMenu: isV9MenuEnabled ? false : true,
  };
  return isNewButtonEnabled ? (
    <RibbonFlyoutAnchorNext ref={ref} {...finalProps} />
  ) : (
    <RibbonFlyoutAnchorCurrent ref={ref as any} {...props} />
  );
});

RibbonFlyoutAnchor.displayName = "RibbonFlyoutAnchor";

export type RibbonFlyoutAnchorType = typeof RibbonFlyoutAnchor.prototype;
